import { supabase } from '../lib/supabase';
import { supabaseRealtimeManager } from './supabaseRealtimeManager';
import { Campaign } from '../pages/brand-dashboard/types';
import { oneSignalService } from './oneSignalService';
import { CPVIntegrationService } from './cpvIntegrationService';

// Helper function to upload campaign image to Supabase Storage
async function uploadCampaignImage(file: File, brandId: string): Promise<string | null> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${brandId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('campaign-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('Error uploading campaign image:', error);
      return null;
    }
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('campaign-images')
      .getPublicUrl(data.path);
    
    return urlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadCampaignImage:', error);
    return null;
  }
}

export const campaignService = {
  /**
   * Subscribe to real-time campaign changes
   */
  subscribeToCampaigns(callback: (payload: any) => void, brandId?: string) {
    supabaseRealtimeManager.subscribe({
      channel: 'campaigns-realtime',
      event: '*',
      schema: 'public',
      table: 'campaigns',
      filter: brandId ? `brand_id=eq.${brandId}` : undefined,
      callback,
    });
  },
  async getAllCampaigns(): Promise<Campaign[]> {
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return data.map(campaign => ({
      id: campaign.id,
      title: campaign.title,
      description: campaign.description,
      budget: campaign.budget,
      spent: campaign.spent,
      status: campaign.status,
      startDate: new Date(campaign.start_date),
      endDate: new Date(campaign.end_date),
      category: campaign.category,
      targetReach: campaign.target_reach,
      currentReach: campaign.current_reach,
      applicationsCount: campaign.applications_count,
      approvedCreators: campaign.approved_creators,
      costPerView: campaign.cost_per_view,
      totalViews: campaign.total_views,
      engagementRate: campaign.engagement_rate,
      roi: campaign.roi,
      image: campaign.image_url || '',
      alt: `${campaign.title} campaign image`
    }));
  },

  async getCampaignsByBrand(brandId: string): Promise<Campaign[]> {
    const { data, error } = await supabase
      .from('campaigns')
      .select('*, deliverable_requirements, content_types')
      .eq('brand_id', brandId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return data.map(campaign => ({
      id: campaign.id,
      title: campaign.title,
      description: campaign.description,
      budget: campaign.budget,
      spent: campaign.spent,
      status: campaign.status,
      startDate: new Date(campaign.start_date),
      endDate: new Date(campaign.end_date),
      category: campaign.category,
      targetReach: campaign.target_reach,
      currentReach: campaign.current_reach,
      applicationsCount: campaign.applications_count,
      approvedCreators: campaign.approved_creators,
      costPerView: campaign.cost_per_view,
      totalViews: campaign.total_views,
      engagementRate: campaign.engagement_rate,
      roi: campaign.roi,
      image: campaign.image_url || '',
      alt: `${campaign.title} campaign image`,
      // Barter campaign fields
      campaign_mode: campaign.campaign_mode || 'paid',
      product_name: campaign.product_name,
      product_value: campaign.product_value,
      product_units: campaign.product_units,
      approved_creators: campaign.approved_creators,
      // Deliverable details
      deliverable_requirements: campaign.deliverable_requirements,
      content_types: campaign.content_types,
    }));
  },

  async createCampaign(campaignData: any, brandId: string): Promise<Campaign> {
    // Calculate cost per view from budget and target reach
    const budget = parseFloat(campaignData.budget) || 0;
    const targetReach = parseInt(campaignData.targetReach) || 0;
    const costPerView = targetReach > 0 ? budget / targetReach : 0;
    
    // Determine if this is a barter campaign
    const isBarterCampaign = campaignData.campaignMode === 'barter';
    
    // Handle dates - use defaults if empty
    const now = new Date();
    const defaultEndDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now
    const startDate = campaignData.startDate && campaignData.startDate !== '' 
      ? campaignData.startDate 
      : now.toISOString();
    const endDate = campaignData.endDate && campaignData.endDate !== '' 
      ? campaignData.endDate 
      : defaultEndDate.toISOString();
    
    // Handle image upload if a file was provided
    let imageUrl = campaignData.imageUrl || null;
    if (campaignData.imageFile instanceof File) {
      const uploadedUrl = await uploadCampaignImage(campaignData.imageFile, brandId);
      if (uploadedUrl) {
        imageUrl = uploadedUrl;
      }
    }

    const { data, error } = await supabase
      .from('campaigns')
      .insert({
        brand_id: brandId,
        title: campaignData.title,
        description: campaignData.description,
        category: campaignData.category,
        budget: isBarterCampaign ? 0 : budget,
        cost_per_view: isBarterCampaign ? 0 : costPerView,
        target_reach: isBarterCampaign ? 0 : targetReach,
        start_date: startDate,
        end_date: endDate,
        requirements: campaignData.requirements,
        target_demographics: campaignData.targetDemographics,
        content_types: campaignData.contentTypes, // Array of content types
        deliverable_requirements: campaignData.deliverableRequirements || [], // Detailed requirements with quantities
        content_guidelines: campaignData.contentGuidelines, // Content guidelines text
        image_url: imageUrl, // Campaign thumbnail (uploaded or URL)
        status: 'active', // Make campaign immediately visible to creators
        spent: 0,
        current_reach: 0,
        applications_count: 0,
        approved_creators: 0,
        total_views: 0,
        engagement_rate: 0,
        roi: 0,
        // Campaign mode specific fields
        campaign_mode: campaignData.campaignMode || 'paid',
        product_name: isBarterCampaign ? campaignData.productName : null,
        product_value: isBarterCampaign ? parseFloat(campaignData.productValue || '0') : null,
        product_units: isBarterCampaign ? parseInt(campaignData.productUnits || '0') : null,
        delivery_instructions: isBarterCampaign ? campaignData.deliveryInstructions : null,
        requirement_checklist: campaignData.requirementChecklist || null,
        // Barter Order & Delivery fields
        require_shipping_address: isBarterCampaign ? campaignData.requireShippingAddress ?? true : null,
        estimated_delivery_days: isBarterCampaign ? parseInt(campaignData.estimatedDeliveryDays || '0') : null,
        shipping_provider: isBarterCampaign ? campaignData.shippingProvider : null,
        delivery_confirmation_required: isBarterCampaign ? campaignData.deliveryConfirmationRequired ?? true : null,
        product_sku: isBarterCampaign ? campaignData.productSKU : null,
        product_category: isBarterCampaign ? campaignData.productCategory : null,
      })
      .select()
      .single();

    if (error) throw error;
    
    // Auto-post to community when campaign is created
    await this.createCampaignCommunityPost(data, brandId);
    
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      budget: data.budget,
      spent: data.spent,
      status: data.status,
      startDate: new Date(data.start_date),
      endDate: new Date(data.end_date),
      category: data.category,
      targetReach: data.target_reach,
      currentReach: data.current_reach,
      applicationsCount: data.applications_count,
      approvedCreators: data.approved_creators,
      costPerView: data.cost_per_view,
      totalViews: data.total_views,
      engagementRate: data.engagement_rate,
      roi: data.roi,
      image: data.image_url || '',
      alt: `${data.title} campaign image`
    };
  },

  /**
   * Create a community post for a new campaign
   */
  async createCampaignCommunityPost(campaignData: any, brandId: string): Promise<void> {
    try {
      // Get brand user info
      const { data: brandProfile } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, avatar_url')
        .eq('id', brandId)
        .single();

      const isBarterCampaign = campaignData.campaign_mode === 'barter';
      const campaignTitle = campaignData.title;
      const productValue = campaignData.product_value;
      const productName = campaignData.product_name;
      
      // Generate community post content
      let postContent = `🚀 **New Campaign Alert!**\n\n`;
      postContent += `📣 **${campaignTitle}**\n\n`;
      
      if (isBarterCampaign && productName) {
        postContent += `🎁 **Barter Collaboration** - ${productName}`;
        if (productValue) {
          postContent += ` (Worth ₹${productValue.toLocaleString()})`;
        }
        postContent += `\n\n`;
      } else if (campaignData.budget > 0) {
        postContent += `💰 **Paid Campaign** - Budget: ₹${campaignData.budget.toLocaleString()}\n\n`;
      }
      
      if (campaignData.description) {
        const shortDesc = campaignData.description.length > 200 
          ? campaignData.description.substring(0, 200) + '...'
          : campaignData.description;
        postContent += `${shortDesc}\n\n`;
      }
      
      postContent += `📱 Apply now on NoonX!\n\n`;
      postContent += `#NoonXCampaign #CreatorOpportunity #${campaignData.category || 'Collab'}`;

      // Create community post
      const { error } = await supabase
        .from('community_posts')
        .insert({
          user_id: brandId,
          content: postContent,
          image_url: campaignData.image_url || null,
          campaign_id: campaignData.id,
        });

      if (error) {
        console.warn('Could not create community post for campaign:', error.message);
      } else {
        console.log('Community post created for campaign:', campaignData.id);
      }
    } catch (error) {
      console.warn('Error creating community post:', error);
    }
  },

  /**
   * Create an external campaign imported from WhatsApp/other sources
   * These campaigns have external Google Form URLs for applications
   */
  async createExternalCampaign(campaignData: any, brandId: string): Promise<Campaign> {
    const isBarterCampaign = campaignData.campaignMode === 'barter';
    const productValue = parseFloat(campaignData.productValue || '0');
    
    const { data, error } = await supabase
      .from('campaigns')
      .insert({
        brand_id: brandId,
        title: campaignData.title,
        description: campaignData.description,
        category: campaignData.category || 'lifestyle',
        budget: 0, // External campaigns don't use NoonX budget
        cost_per_view: 0,
        target_reach: 0,
        start_date: campaignData.startDate || new Date().toISOString(),
        end_date: campaignData.endDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        requirements: campaignData.requirements || '',
        target_demographics: campaignData.targetDemographics || '',
        content_types: campaignData.contentTypes || [],
        deliverable_requirements: campaignData.deliverableRequirements || [],
        content_guidelines: campaignData.contentGuidelines || '',
        status: 'active',
        spent: 0,
        current_reach: 0,
        applications_count: 0,
        approved_creators: 0,
        total_views: 0,
        engagement_rate: 0,
        roi: 0,
        // Campaign mode
        campaign_mode: campaignData.campaignMode || 'barter',
        product_name: campaignData.productName,
        product_value: productValue,
        product_units: parseInt(campaignData.productUnits || '1'),
        delivery_instructions: campaignData.deliveryInstructions,
        // External campaign fields
        is_external: true,
        external_form_url: campaignData.externalFormUrl,
        external_form_fields: campaignData.externalFormFields || [],
        source_platform: campaignData.sourcePlatform || 'whatsapp',
        original_text: campaignData.originalText,
        agency_name: campaignData.agencyName,
        agency_contact: campaignData.agencyContact,
        parsed_eligibility: campaignData.parsedEligibility || {},
        noonx_branding_text: 'Applied via NoonX Platform - India\'s #1 Creator Marketplace',
      })
      .select()
      .single();

    if (error) throw error;
    
    // Auto-post to community when external campaign is created
    await this.createCampaignCommunityPost(data, brandId);
    
    // If agency info provided, try to track it for lead generation (non-blocking)
    if (campaignData.agencyName) {
      try {
        // First check if external_agencies table exists
        const { error: tableCheckError } = await supabase
          .from('external_agencies')
          .select('id')
          .limit(1);
        
        // Only attempt upsert if table exists
        if (!tableCheckError) {
          await supabase
            .from('external_agencies')
            .upsert({
              name: campaignData.agencyName,
              contact_info: campaignData.agencyContact || null,
              campaigns_count: 1,
              last_campaign_at: new Date().toISOString(),
            }, {
              onConflict: 'name',
            })
            .then(({ error: agencyError }) => {
              if (agencyError) {
                console.warn('Could not track agency:', agencyError.message);
              }
            });
        } else {
          console.warn('external_agencies table not available:', tableCheckError.message);
        }
      } catch (e) {
        console.warn('Agency tracking skipped:', e);
      }
    }
    
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      budget: data.budget,
      spent: data.spent,
      status: data.status,
      startDate: new Date(data.start_date),
      endDate: new Date(data.end_date),
      category: data.category,
      targetReach: data.target_reach,
      currentReach: data.current_reach,
      applicationsCount: data.applications_count,
      approvedCreators: data.approved_creators,
      costPerView: data.cost_per_view,
      totalViews: data.total_views,
      engagementRate: data.engagement_rate,
      roi: data.roi,
      image: data.image_url || '',
      alt: `${data.title} campaign image`
    };
  },

  /**
   * Get all external campaigns (for creator discovery)
   */
  async getExternalCampaigns(): Promise<Campaign[]> {
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('is_external', true)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return data.map(campaign => ({
      id: campaign.id,
      title: campaign.title,
      description: campaign.description,
      budget: campaign.budget,
      spent: campaign.spent,
      status: campaign.status,
      startDate: new Date(campaign.start_date),
      endDate: new Date(campaign.end_date),
      category: campaign.category,
      targetReach: campaign.target_reach,
      currentReach: campaign.current_reach,
      applicationsCount: campaign.applications_count,
      approvedCreators: campaign.approved_creators,
      costPerView: campaign.cost_per_view,
      totalViews: campaign.total_views,
      engagementRate: campaign.engagement_rate,
      roi: campaign.roi,
      image: campaign.image_url || '',
      alt: `${campaign.title} campaign image`,
      // External campaign specific fields
      isExternal: campaign.is_external,
      externalFormUrl: campaign.external_form_url,
      externalFormFields: campaign.external_form_fields,
      sourcePlatform: campaign.source_platform,
      agencyName: campaign.agency_name,
      parsedEligibility: campaign.parsed_eligibility,
      productValue: campaign.product_value,
      productName: campaign.product_name,
    }));
  },

  // Get single campaign with full details - ENHANCED WITH NEW BACKEND
  async getCampaignById(campaignId: string) {
    try {
      console.log('📥 getCampaignById called with:', campaignId);
      
      // Try campaigns table first
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', campaignId)
        .maybeSingle();
      
      if (campaignData) {
        console.log('✅ Campaign found in campaigns table:', campaignData);
        return campaignData;
      }
      
      // If not found, try agency_campaigns table
      console.log('🔍 Campaign not in campaigns table, checking agency_campaigns...');
      const { data: agencyData, error: agencyError } = await supabase
        .from('agency_campaigns')
        .select('*')
        .eq('id', campaignId)
        .maybeSingle();
      
      if (agencyData) {
        console.log('✅ Campaign found in agency_campaigns table:', agencyData);
        
        // Fetch agency details
        let agencyInfo = null;
        if (agencyData.agency_id) {
          const { data: agency } = await supabase
            .from('user_profiles')
            .select('id, full_name, username, avatar_url, company_name')
            .eq('id', agencyData.agency_id)
            .maybeSingle();
          agencyInfo = agency;
        }
        
        // Fetch client (brand owner) details
        let clientInfo = null;
        if (agencyData.client_id) {
          const { data: client } = await supabase
            .from('agency_clients')
            .select('id, name, logo_url, contact_email, website')
            .eq('id', agencyData.client_id)
            .maybeSingle();
          clientInfo = client;
        }
        
        // Fetch brand details
        let brandInfo = null;
        if (agencyData.brand_id) {
          try {
            const { data: brand, error: brandError } = await supabase
              .from('agency_client_brands')
              .select('id, name, logo_url')
              .eq('id', agencyData.brand_id)
              .maybeSingle();
            if (!brandError) {
              brandInfo = brand;
            } else {
              console.warn('Brand fetch warning:', brandError.message);
            }
          } catch (brandErr) {
            console.warn('Brand fetch error:', brandErr);
          }
        }
        
        // Transform agency campaign to match expected format
        return {
          ...agencyData,
          name: agencyData.title,
          guidelines: agencyData.content_guidelines,
          budget: agencyData.total_budget,
          requirements: agencyData.deliverables,
          // Agency and brand info for creators to see
          isAgencyCampaign: true,
          agency: agencyInfo,
          agencyName: agencyInfo?.company_name || agencyInfo?.full_name || 'Agency',
          agencyLogo: agencyInfo?.avatar_url,
          client: clientInfo,
          clientName: clientInfo?.name || 'Client',
          clientLogo: clientInfo?.logo_url,
          brand: brandInfo,
          brandName: brandInfo?.name || clientInfo?.name || 'Brand',
          brandLogo: brandInfo?.logo_url || clientInfo?.logo_url,
          brandDescription: brandInfo?.description,
          brandWebsite: brandInfo?.website || clientInfo?.website,
          brandIndustry: brandInfo?.industry,
        };
      }
      
      console.error('❌ Campaign not found in either table');
      return null;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      return null;
    }
  },

  // Update campaign
  async updateCampaign(campaignId: string, updates: any) {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', campaignId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  },

  // Get campaign stats
  async getCampaignStats(campaignId: string) {
    try {
      const [applications, creators, submissions, payments, creatorsPayments] = await Promise.all([
        supabase.from('campaign_applications').select('*', { count: 'exact', head: true }).eq('campaign_id', campaignId),
        supabase.from('campaign_creators').select('*', { count: 'exact', head: true }).eq('campaign_id', campaignId),
        supabase.from('content_submissions').select('*', { count: 'exact', head: true }).eq('campaign_id', campaignId),
        supabase.from('payments').select('amount, status').eq('campaign_id', campaignId),
        // Also get payment amounts from campaign_creators to calculate pending payments
        supabase.from('campaign_creators').select('payment_amount, status').eq('campaign_id', campaignId)
      ]);

      // Calculate paid amount from payments table
      const paidFromPayments = payments.data?.filter((p: any) => p.status === 'paid').reduce((sum: number, p: any) => sum + (p.amount || 0), 0) || 0;
      
      // Calculate paid amount from campaign_creators (using status = 'completed' as proxy for paid)
      const paidFromCreators = creatorsPayments.data?.filter((c: any) => c.status === 'completed').reduce((sum: number, c: any) => sum + (c.payment_amount || 0), 0) || 0;
      
      // Calculate total pending payment amount (for display)
      const pendingFromCreators = creatorsPayments.data?.filter((c: any) => c.status !== 'completed').reduce((sum: number, c: any) => sum + (c.payment_amount || 0), 0) || 0;
      
      // Total paid is from both sources (avoiding double-counting)
      const totalPaid = Math.max(paidFromPayments, paidFromCreators);
      
      return {
        total_applications: applications.count || 0,
        total_approved: creators.count || 0,
        total_submissions: submissions.count || 0,
        paid_amount: totalPaid,
        pending_amount: pendingFromCreators
      };
    } catch (error) {
      console.error('Error fetching campaign stats:', error);
      return {
        total_applications: 0,
        total_approved: 0,
        total_submissions: 0,
        paid_amount: 0,
        pending_amount: 0
      };
    }
  },

  // Applications Management
  async getApplications(campaignId: string, status?: string) {
    try {
      console.log('🔍 Fetching applications for campaign:', campaignId, 'status filter:', status);
      
      // First check if we can access the table at all
      const { data: testData, error: testError } = await supabase
        .from('campaign_applications')
        .select('count')
        .eq('campaign_id', campaignId)
        .limit(1);
      
      console.log('🔍 RLS Test:', { testData, testError, campaignId });
      
      // Use simple query without nested joins to avoid RLS issues
      let query = supabase
        .from('campaign_applications')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('applied_at', { ascending: false });

      if (status && status !== 'all' && status !== 'pending') {
        query = query.eq('status', status);
      } else if (status === 'pending') {
        query = query.eq('status', 'pending');
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('❌ Error fetching applications:', error);
        throw error;
      }
      
      console.log('📊 Raw applications fetched:', data?.length || 0, 'data:', data);

      // Manually enrich each application with creator data
      const enrichedData = await Promise.all(
        (data || []).map(async (app: any) => {
          try {
            console.log('🔄 Enriching application:', app.id, 'creator_id:', app.creator_id);
            
            // Fetch creator profile - including both legacy and instagram-specific columns
            // Using maybeSingle() to avoid PGRST116 error when no profile exists
            const { data: creatorProfile, error: creatorError } = await supabase
              .from('creator_profiles')
              .select('id, user_id, username, category, followers, engagement_rate, average_views, rating, instagram_followers, instagram_engagement_rate, instagram_handle')
              .eq('id', app.creator_id)
              .maybeSingle();

            if (creatorError) {
              console.error('❌ Could not fetch creator profile for:', app.creator_id, {
                error: creatorError,
                message: creatorError.message,
                details: creatorError.details,
                hint: creatorError.hint,
                code: creatorError.code
              });
              // Don't return null, try to continue with whatever data we have
              // return {
              //   ...app,
              //   creator: null
              // };
            }

            // If no creator profile found, create a minimal one
            const mergedProfile = creatorProfile ? {
              ...creatorProfile,
              followers: creatorProfile.followers || creatorProfile.instagram_followers || 0,
              engagement_rate: creatorProfile.engagement_rate || creatorProfile.instagram_engagement_rate || 0,
              instagram_handle: creatorProfile.instagram_handle, // Keep instagram handle for display
            } : {
              id: app.creator_id,
              user_id: app.creator_id, // Fallback: assume creator_id is user_id
              username: null,
              category: 'General',
              followers: 0,
              engagement_rate: 0,
              instagram_handle: null
            };

            console.log('✅ Creator profile fetched:', mergedProfile.id, 'user_id:', mergedProfile.user_id, 'followers:', mergedProfile.followers, 'instagram_followers:', creatorProfile?.instagram_followers);

            // Fetch user profile - using maybeSingle() to avoid PGRST116 error when no profile exists
            const { data: userProfile, error: userError } = await supabase
              .from('user_profiles')
              .select('id, full_name, username, avatar_url, email')
              .eq('id', mergedProfile.user_id)
              .maybeSingle();

            if (userError) {
              console.error('❌ Could not fetch user profile for:', mergedProfile.user_id, {
                error: userError,
                message: userError.message,
                details: userError.details,
                hint: userError.hint,
                code: userError.code
              });
            } else {
              console.log('✅ User profile fetched:', {
                id: userProfile?.id,
                full_name: userProfile?.full_name,
                username: userProfile?.username,
                email: userProfile?.email
              });
            }

            const enriched = {
              ...app,
              creator: {
                ...mergedProfile,
                user: userProfile || null
              }
            };
            
            console.log('✅ Final enriched application:', {
              id: enriched.id,
              creator_id: app.creator_id,
              creator_username: enriched.creator?.username,
              creator_category: enriched.creator?.category,
              creator_followers: enriched.creator?.followers,
              creator_engagement_rate: enriched.creator?.engagement_rate,
              user_full_name: enriched.creator?.user?.full_name,
              user_username: enriched.creator?.user?.username
            });
            
            return enriched;
          } catch (err) {
            console.error('❌ Error enriching application:', app.id, err);
            return {
              ...app,
              creator: null
            };
          }
        })
      );

      console.log('✅ Enriched applications:', enrichedData.length, 'with creator data');
      return enrichedData;
    } catch (error: any) {
      console.error('❌ Error in getApplications:', {
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code,
        campaignId,
        fullError: error
      });
      return [];
    }
  },

  async approveApplication(applicationId: string, hints?: { campaignId: string; creatorId: string; payoutAmount?: number }) {
    try {
      console.log('🔧 approveApplication called:', { applicationId, hints });
      
      // Build update object
      const updateData: any = { 
        status: 'approved', 
        updated_at: new Date().toISOString() 
      };
      
      // If payoutAmount provided, set payout_offered
      if (hints?.payoutAmount !== undefined && hints.payoutAmount > 0) {
        updateData.payout_offered = hints.payoutAmount;
      }
      
      // 1) Update application status to approved (and payout_offered if provided)
      const { error } = await supabase
        .from('campaign_applications')
        .update(updateData)
        .eq('id', applicationId);
        
      if (error) {
        // Check if this is the deliverable_submissions RLS error from trigger
        const errorMessage = (error as any).message || '';
        if (errorMessage.includes('deliverable_submissions')) {
          console.warn('⚠️ Trigger RLS error (non-fatal), approval may have succeeded');
          // The approval itself might have worked, continue with the rest
        } else {
          console.error('❌ Error approving application:', {
            message: (error as any).message,
            details: (error as any).details,
            hint: (error as any).hint,
            code: (error as any).code,
            applicationId
          });
          throw error;
        }
      }

      console.log('✅ Application status updated to approved');

      // 2) Ensure the creator appears in Approved Creators tab
      const campaignId = hints?.campaignId;
      const creatorId = hints?.creatorId;

      console.log('🔧 Inserting into campaign_creators:', { campaignId, creatorId });

      if (campaignId && creatorId) {
        // Use the payoutAmount from hints if provided, otherwise fetch from application
        let payoutAmount = hints?.payoutAmount || 0;
        
        if (!payoutAmount) {
          // Fallback: get the payout amount from application
          const { data: appData, error: appError } = await supabase
            .from('campaign_applications')
            .select('payout_offered, proposed_budget')
            .eq('id', applicationId)
            .maybeSingle(); // Use maybeSingle to avoid 406 if row doesn't exist
          
          if (appError) {
            console.warn('⚠️ Could not fetch application for payout:', appError);
          }
          
          // Use payout_offered or proposed_budget - don't fall back to campaign total budget
          payoutAmount = appData?.payout_offered || appData?.proposed_budget || 0;
        }

        // Upsert link; rely on unique(campaign_id, creator_id)
        const { data: insertedData, error: upsertErr } = await supabase
          .from('campaign_creators')
          .upsert({
            campaign_id: campaignId,
            creator_id: creatorId,
            status: 'not_started',
            approved_at: new Date().toISOString(),
            source: 'application', // Track that creator joined via application
            payment_amount: payoutAmount // Include the creator's agreed payment
          }, { onConflict: 'campaign_id,creator_id', ignoreDuplicates: true })
          .select();
          
        if (upsertErr) {
          console.error('❌ Error upserting campaign_creators:', upsertErr);
        } else {
          console.log('✅ Successfully inserted into campaign_creators:', insertedData);
        }

        // 3) Calculate and store dynamic CPV for creator
        try {
          const cpvResult = await CPVIntegrationService.calculateAndStoreCPV(
            campaignId,
            creatorId,
            payoutAmount > 0 ? payoutAmount / 10000 : 0.05 // Rough estimate if no payout set
          );
          
          console.log('💰 Dynamic CPV calculated and stored:', {
            baseCPV: cpvResult.baseCPV,
            adjustedCPV: cpvResult.adjustedCPV,
            tier: cpvResult.performanceTier,
            multiplier: cpvResult.performanceMultiplier
          });
        } catch (cpvError) {
          console.warn('⚠️ Could not calculate dynamic CPV (non-blocking):', cpvError);
        }

        // 4) Create pending payment record
        try {
          await supabase
            .from('payments')
            .upsert({
              campaign_id: campaignId,
              creator_id: creatorId,
              amount: payoutAmount,
              status: 'pending',
              created_at: new Date().toISOString()
            }, { onConflict: 'campaign_id,creator_id' });
          
          console.log('✅ Pending payment created:', { campaignId, creatorId, amount: payoutAmount });
        } catch (paymentError) {
          console.warn('⚠️ Could not create payment record:', paymentError);
        }

        // 4) Manually update campaign metrics
        try {
          const { data: campaign } = await supabase
            .from('campaigns')
            .select('applications_count, approved_creators')
            .eq('id', campaignId)
            .single();

          if (campaign) {
            await supabase
              .from('campaigns')
              .update({
                approved_creators: (campaign.approved_creators || 0) + 1,
                updated_at: new Date().toISOString()
              })
              .eq('id', campaignId);
          }
        } catch (metricsError) {
          console.warn('⚠️ Could not update campaign metrics:', metricsError);
        }
      }
      return true;
    } catch (error: any) {
      throw error;
    }
  },

  async rejectApplication(applicationId: string, hints?: { campaignId?: string }) {
    try {
      const { error } = await supabase
        .from('campaign_applications')
        .update({ status: 'rejected', updated_at: new Date().toISOString() })
        .eq('id', applicationId);

      if (error) throw error;
      
      // Manually update campaign metrics if campaignId provided (workaround for missing trigger)
      if (hints?.campaignId) {
        try {
          const { data: campaign } = await supabase
            .from('campaigns')
            .select('applications_count, approved_creators')
            .eq('id', hints.campaignId)
            .single();

          if (campaign) {
            // Update the updated_at timestamp to trigger any UI refreshes
            await supabase
              .from('campaigns')
              .update({
                updated_at: new Date().toISOString()
              })
              .eq('id', hints.campaignId);
          }
        } catch (metricsError) {
          console.warn('⚠️ Could not update campaign metrics:', metricsError);
          // Non-fatal - continue even if metrics update fails
        }
      }
      
      return true;
    } catch (error: any) {
      throw error;
    }
  },

  // Update approved creator status (by campaign_creators.id OR campaign_applications.id - used by brand)
  async updateCreatorStatus(
    id: string, 
    status: 'not_started' | 'working' | 'submitted' | 'completed',
    hints?: { campaignId?: string; creatorId?: string }
  ) {
    try {
      console.log('🔄 Updating creator status:', { id, status, hints });
      
      // Map status to valid application status values
      // campaign_applications.status is an ENUM: 'pending', 'approved', 'rejected', 'completed'
      // campaign_applications.deliverable_status: 'not_started', 'submitted', 'approved', 'revision_requested', 'paid'
      const appStatus = status === 'working' ? 'approved' : 
                       status === 'submitted' ? 'completed' :
                       status === 'completed' ? 'completed' : 'approved';
      
      // Map to valid deliverable_status values (NOT 'working' or 'completed')
      const deliverableStatus = status === 'not_started' ? 'not_started' :
                                status === 'working' ? 'not_started' :  // working = still in progress
                                status === 'submitted' ? 'submitted' :
                                status === 'completed' ? 'approved' : 'not_started';
      
      // First try updating campaign_creators by id (this table uses TEXT status, not enum)
      const { data: ccData, error: ccError } = await supabase
        .from('campaign_creators')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('campaign_id, creator_id');

      // If campaign_creators update succeeded and found rows
      if (!ccError && ccData && ccData.length > 0) {
        console.log('✅ Updated campaign_creators:', ccData[0]);
        
        // Try to get application id and use RPC
        const { data: appRecord } = await supabase
          .from('campaign_applications')
          .select('id')
          .eq('campaign_id', ccData[0].campaign_id)
          .eq('creator_id', ccData[0].creator_id)
          .maybeSingle();
        
        if (appRecord?.id) {
          // Use RPC to update enum status properly
          const { error: rpcError } = await supabase.rpc('update_application_status', {
            app_id: appRecord.id,
            new_status: appStatus,
            deliverable_json: { deliverable_status: deliverableStatus }
          });
          
          if (rpcError) {
            console.warn('⚠️ RPC update failed, trying direct update:', rpcError);
            // Fallback: update only deliverable_status (text field)
            await supabase
              .from('campaign_applications')
              .update({ 
                deliverable_status: deliverableStatus,
                updated_at: new Date().toISOString()
              })
              .eq('id', appRecord.id);
          } else {
            console.log('✅ Status synced via RPC:', appStatus, deliverableStatus);
          }
        }
        
        return true;
      }
      
      // If no rows in campaign_creators, try campaign_applications instead
      console.log('📝 No campaign_creators found, trying campaign_applications...');
      
      // Use RPC to handle the enum type properly
      const { error: rpcError } = await supabase.rpc('update_application_status', {
        app_id: id,
        new_status: appStatus,
        deliverable_json: { deliverable_status: deliverableStatus }
      });

      if (rpcError) {
        console.error('❌ RPC update failed:', rpcError);
        
        // Fallback: try updating only deliverable_status (not the enum status)
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('campaign_applications')
          .update({ 
            deliverable_status: deliverableStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
          .select('campaign_id, creator_id');
        
        if (fallbackError) {
          console.error('❌ Fallback update also failed:', fallbackError);
          throw fallbackError;
        }
        
        console.log('✅ Updated deliverable_status (fallback):', fallbackData);
        
        // Also sync to campaign_creators in fallback path
        if (fallbackData && fallbackData.length > 0) {
          const appData = fallbackData[0];
          const { data: existingCC } = await supabase
            .from('campaign_creators')
            .select('id')
            .eq('campaign_id', appData.campaign_id)
            .eq('creator_id', appData.creator_id)
            .maybeSingle();
          
          if (existingCC) {
            await supabase
              .from('campaign_creators')
              .update({ status, updated_at: new Date().toISOString() })
              .eq('id', existingCC.id);
            console.log('✅ Synced status to campaign_creators (fallback path):', status);
          } else {
            await supabase
              .from('campaign_creators')
              .insert({
                campaign_id: appData.campaign_id,
                creator_id: appData.creator_id,
                status: status,
                approved_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
            console.log('✅ Created campaign_creators record (fallback path):', status);
          }
        }
        
        return true;
      }
      
      console.log('✅ Updated campaign_applications via RPC');
      
      // Get the application data for syncing to campaign_creators
      // Use hints if provided (bypasses RLS issues)
      let appData: { campaign_id: string; creator_id: string } | null = null;
      
      if (hints?.campaignId && hints?.creatorId) {
        appData = { campaign_id: hints.campaignId, creator_id: hints.creatorId };
        console.log('📋 Using hints for sync:', appData);
      } else {
        const { data: fetchedData, error: appDataError } = await supabase
          .from('campaign_applications')
          .select('campaign_id, creator_id')
          .eq('id', id)
          .maybeSingle();
        
        appData = fetchedData;
        console.log('📋 App data for sync (from query):', { appData, appDataError, id });
      }
      
      if (appData) {
        console.log('🔄 Syncing status to campaign_creators:', { campaign_id: appData.campaign_id, creator_id: appData.creator_id, status });
        
        // Use RPC function to bypass RLS issues
        const { data: rpcResult, error: rpcUpdateError } = await supabase.rpc('update_campaign_creator_status', {
          p_campaign_id: appData.campaign_id,
          p_creator_id: appData.creator_id,
          p_status: status
        });
        
        console.log('🔄 RPC update result:', { rpcResult, rpcUpdateError });
        
        if (rpcUpdateError) {
          console.warn('⚠️ RPC update failed, trying direct update:', rpcUpdateError);
          
          // Fallback to direct update
          const { data: existingCC, error: ccCheckError } = await supabase
            .from('campaign_creators')
            .select('id')
            .eq('campaign_id', appData.campaign_id)
            .eq('creator_id', appData.creator_id)
            .maybeSingle();
          
          console.log('🔍 Existing campaign_creators check:', { existingCC, ccCheckError });
          
          if (existingCC) {
            // Update existing record
            const { error: updateError } = await supabase
              .from('campaign_creators')
              .update({
                status: status,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingCC.id);
            
            if (updateError) {
              console.warn('⚠️ Could not update campaign_creators:', updateError);
            } else {
              console.log('✅ Updated campaign_creators status:', status);
            }
          } else {
            // Insert new record with required fields
            const { error: insertError } = await supabase
              .from('campaign_creators')
              .insert({
                campaign_id: appData.campaign_id,
                creator_id: appData.creator_id,
                status: status,
                approved_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
            
            if (insertError) {
              console.warn('⚠️ Could not insert campaign_creators:', insertError);
            } else {
              console.log('✅ Created campaign_creators record with status:', status);
            }
          }
        } else {
          console.log('✅ Updated campaign_creators status via RPC:', status, 'result:', rpcResult);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error updating creator status:', error);
      throw error;
    }
  },

  // Update creator's own campaign status (used by creator dashboard)
  async updateCreatorCampaignStatus(
    campaignId: string, 
    creatorId: string, 
    status: 'not_started' | 'working' | 'submitted' | 'completed'
  ) {
    try {
      console.log('🔄 Updating creator campaign status:', { campaignId, creatorId, status });
      
      // Update campaign_creators table (uses TEXT status, not enum)
      const { error } = await supabase
        .from('campaign_creators')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('campaign_id', campaignId)
        .eq('creator_id', creatorId);

      if (error) throw error;
      
      // Map the status to campaign_applications status
      const appStatus = status === 'working' ? 'approved' : 
                       status === 'submitted' ? 'completed' :
                       status === 'completed' ? 'completed' : 'approved';
      
      // Try RPC first to handle enum properly
      try {
        const { data: appData } = await supabase
          .from('campaign_applications')
          .select('id')
          .eq('campaign_id', campaignId)
          .eq('creator_id', creatorId)
          .maybeSingle();
        
        if (appData?.id) {
          const { error: rpcError } = await supabase.rpc('update_application_status', {
            app_id: appData.id,
            new_status: appStatus,
            deliverable_json: null
          });
          
          if (!rpcError) {
            console.log('✅ Status updated via RPC');
          } else {
            console.log('⚠️ RPC failed, using fallback');
            // Fallback: Update non-enum fields only
            await supabase
              .from('campaign_applications')
              .update({ 
                deliverable_status: status === 'submitted' ? 'submitted' : 
                                   status === 'completed' ? 'approved' : 'pending',
                updated_at: new Date().toISOString()
              })
              .eq('campaign_id', campaignId)
              .eq('creator_id', creatorId);
          }
        }
      } catch (syncError) {
        console.warn('⚠️ Could not sync to campaign_applications:', syncError);
      }
      
      console.log('✅ Status updated for campaign_creators');
      return true;
    } catch (error) {
      console.error('Error updating creator campaign status:', error);
      throw error;
    }
  },

  // Approved Creators Management
  async getApprovedCreators(campaignId: string) {
    try {
      console.log('🔍 Fetching approved creators for campaign:', campaignId);
      
      // Reuse getApplications logic but filter for approved status
      const allApps = await this.getApplications(campaignId, 'all');
      
      // Filter to only approved applications (or completed)
      const approvedApps = allApps.filter((app: any) => 
        app.status === 'approved' || app.status === 'completed' || app.status === 'submitted'
      );
      
      console.log('📦 Approved applications from getApplications:', approvedApps.length);
      if (approvedApps.length > 0) {
        console.log('📦 First approved app sample:', {
          id: approvedApps[0].id,
          creator_id: approvedApps[0].creator_id,
          creator: approvedApps[0].creator,
          status: approvedApps[0].status
        });
      }
      
      // Also check campaign_creators table
      const { data: ccData, error: ccError } = await supabase
        .from('campaign_creators')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('approved_at', { ascending: false });

      console.log('📦 campaign_creators query result:', { 
        count: ccData?.length || 0, 
        error: ccError,
        data: ccData 
      });

      // Combine results
      const allCreators: any[] = [];
      const seenCreatorIds = new Set<string>();
      
      // Build a map of campaign_creators statuses and payment amounts (source of truth for updates)
      const ccStatusMap = new Map<string, string>();
      const ccPaymentMap = new Map<string, number>();
      if (ccData && ccData.length > 0) {
        for (const cc of ccData) {
          ccStatusMap.set(cc.creator_id, cc.status);
          if (cc.payment_amount) {
            ccPaymentMap.set(cc.creator_id, cc.payment_amount);
          }
        }
        console.log('📊 campaign_creators status map:', Object.fromEntries(ccStatusMap));
        console.log('💰 campaign_creators payment map:', Object.fromEntries(ccPaymentMap));
      }

      // Add from approved applications first (this has the enriched creator data)
      for (const app of approvedApps) {
        const creatorId = app.creator?.id || app.creator_id;
        if (creatorId && !seenCreatorIds.has(creatorId)) {
          seenCreatorIds.add(creatorId);
          
          // Try to get creator name from RPC if user data is missing
          let creatorName = app.creator?.user?.full_name || app.creator?.user?.username || app.creator?.username;
          
          if (!creatorName || creatorName === 'Unknown Creator') {
            try {
              const { data: rpcName } = await supabase.rpc('get_creator_display_name', { p_creator_id: creatorId });
              if (rpcName) {
                creatorName = rpcName;
                console.log('✅ Got creator name from RPC:', creatorName);
              }
            } catch (e) {
              console.log('⚠️ RPC name lookup failed:', e);
            }
          }
          
          // Use campaign_creators status as source of truth, fallback to deliverable_status
          const ccStatus = ccStatusMap.get(creatorId);
          const finalStatus = ccStatus || app.deliverable_status || 'not_started';
          
          // Use payout_offered (agreed amount) as source of truth for payment
          const ccPayment = ccPaymentMap.get(creatorId);
          const finalPayment = app.payout_offered || ccPayment || app.proposed_budget || 0;
          
          console.log('🔄 Processing approved app:', {
            id: app.id,
            creatorId,
            creatorName,
            ccStatus,
            deliverable_status: app.deliverable_status,
            finalStatus,
            ccPayment,
            payout_offered: app.payout_offered,
            finalPayment,
            avatar_url: app.creator?.user?.avatar_url,
            user_full_name: app.creator?.user?.full_name,
            user_username: app.creator?.user?.username
          });

          allCreators.push({
            id: app.id,
            campaign_id: campaignId,
            creator_id: creatorId,
            status: finalStatus,
            creator: {
              id: app.creator?.id || creatorId,
              user_id: app.creator?.user_id,
              username: app.creator?.username || app.creator?.user?.username || creatorName,
              category: app.creator?.category || 'General',
              niche: app.creator?.niche,
              followers: app.creator?.followers || 0,
              engagement_rate: app.creator?.engagement_rate || 0,
              name: creatorName, // Add name directly for fallback
              user: {
                id: app.creator?.user?.id,
                full_name: app.creator?.user?.full_name || creatorName, // Keep original full_name if it exists
                username: app.creator?.user?.username || app.creator?.username || creatorName,
                avatar_url: app.creator?.user?.avatar_url,
                email: app.creator?.user?.email
              }
            },
            deliverables_description: app.message || 'TBD',
            payment_amount: finalPayment,
            content_deadline: app.deadline,
            source: 'application'
          });
        }
      }

      // Add from campaign_creators if any exist
      if (ccData && ccData.length > 0) {
        for (const cc of ccData) {
          if (!seenCreatorIds.has(cc.creator_id)) {
            seenCreatorIds.add(cc.creator_id);
            
            console.log('🔄 Processing campaign_creators entry:', cc.creator_id);
            
            // Fetch creator and user profiles for campaign_creators entries
            const { data: creatorProfile, error: cpError } = await supabase
              .from('creator_profiles')
              .select('id, user_id, username, category, followers, engagement_rate, instagram_handle')
              .eq('id', cc.creator_id)
              .single();

            if (cpError) {
              console.error('❌ Error fetching creator_profile for campaign_creator:', cc.creator_id, cpError);
            } else {
              console.log('✅ Creator profile fetched for campaign_creator:', creatorProfile);
            }

            let userProfile = null;
            if (creatorProfile?.user_id) {
              const { data: up, error: upError } = await supabase
                .from('user_profiles')
                .select('id, full_name, username, avatar_url, email')
                .eq('id', creatorProfile.user_id)
                .single();
              userProfile = up;
              
              if (upError) {
                console.error('❌ Error fetching user_profile for campaign_creator:', creatorProfile.user_id, upError);
              } else {
                console.log('✅ User profile fetched for campaign_creator:', up);
              }
            }
            
            // Try RPC fallback if no name found
            let creatorName = userProfile?.full_name || userProfile?.username || creatorProfile?.username || creatorProfile?.instagram_handle;
            if (!creatorName) {
              try {
                const { data: rpcName } = await supabase.rpc('get_creator_display_name', { p_creator_id: cc.creator_id });
                if (rpcName) {
                  creatorName = rpcName;
                  console.log('✅ Got creator name from RPC for campaign_creator:', creatorName);
                }
              } catch (e) {
                console.log('⚠️ RPC name lookup failed for campaign_creator:', e);
              }
            }

            allCreators.push({
              ...cc,
              creator: {
                id: creatorProfile?.id || cc.creator_id,
                user_id: creatorProfile?.user_id,
                username: creatorProfile?.username || creatorName,
                category: creatorProfile?.category || 'General',
                instagram_handle: creatorProfile?.instagram_handle,
                followers: creatorProfile?.followers || 0,
                engagement_rate: creatorProfile?.engagement_rate || 0,
                name: creatorName, // Direct name fallback
                user: userProfile ? {
                  ...userProfile,
                  full_name: userProfile.full_name || creatorName
                } : {
                  id: creatorProfile?.user_id,
                  full_name: creatorName,
                  username: creatorName,
                  avatar_url: null,
                  email: null
                }
              },
              deliverables_description: cc.deliverables_description || 'TBD',
              payment_amount: cc.payment_amount || 0,
              source: cc.source || 'invitation'
            });
          }
        }
      }

      console.log('✅ Total approved creators:', allCreators.length);
      return allCreators;
    } catch (error) {
      console.error('Error fetching approved creators:', error);
      return [];
    }
  },

  // Content Submissions Management
  async getSubmissions(campaignId: string) {
    try {
      console.log('📦 Fetching submissions for campaign:', campaignId);
      
      // FIRST: Check deliverable_submissions table (from mobile app)
      console.log('🔄 Fetching from deliverable_submissions table...');
      const { data: deliverableData, error: deliverableError } = await supabase
        .from('deliverable_submissions')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('submitted_at', { ascending: false });
      
      if (!deliverableError && deliverableData && deliverableData.length > 0) {
        console.log('✅ Found submissions in deliverable_submissions:', deliverableData.length);
        console.log('📦 Raw deliverable_submissions data:', JSON.stringify(deliverableData, null, 2));
        
        // Group by application_id and transform
        const submissionsByApp: { [key: string]: any[] } = {};
        deliverableData.forEach((sub: any) => {
          if (!submissionsByApp[sub.application_id]) {
            submissionsByApp[sub.application_id] = [];
          }
          submissionsByApp[sub.application_id].push(sub);
        });
        
        // Transform to expected format
        const transformedSubmissions = await Promise.all(
          Object.entries(submissionsByApp).map(async ([appId, subs]) => {
            const firstSub = subs[0];
            
            // Get application details
            const { data: appData } = await supabase
              .from('campaign_applications')
              .select('*')
              .eq('id', appId)
              .single();
            
            // Get creator info using RPC function (bypasses RLS)
            let creatorName = 'Creator';
            let creatorAvatar = '';
            
            console.log('🔍 Looking up creator via RPC, creator_id:', firstSub.creator_id);
            
            try {
              // Use RPC function that has SECURITY DEFINER
              const { data: nameData, error: nameError } = await supabase
                .rpc('get_creator_display_name', { p_creator_id: firstSub.creator_id });
              
              if (!nameError && nameData) {
                creatorName = nameData;
                console.log('✅ Got creator name from RPC:', creatorName);
              } else {
                console.log('⚠️ RPC failed:', nameError, '- trying fallback');
                
                // Fallback: Try to get username from creator_profiles, then full_name from user_profiles
                const { data: creatorProfile } = await supabase
                  .from('creator_profiles')
                  .select('username, user_id')
                  .eq('id', firstSub.creator_id)
                  .maybeSingle();
                
                if (creatorProfile) {
                  creatorName = creatorProfile.username || 'Creator';
                  
                  // Try to get full name from user_profiles
                  if (creatorProfile.user_id) {
                    const { data: userProfile } = await supabase
                      .from('user_profiles')
                      .select('full_name, username, avatar_url')
                      .eq('id', creatorProfile.user_id)
                      .maybeSingle();
                    
                    if (userProfile) {
                      creatorName = userProfile.full_name || userProfile.username || creatorName;
                      creatorAvatar = userProfile.avatar_url || '';
                    }
                  }
                }
              }
              
              console.log('👤 Final creator name:', creatorName);
            } catch (e) {
              console.warn('Could not fetch creator info:', e);
            }
            
            // Collect all content URLs
            const contentUrls = subs.map((s: any) => s.content_url).filter(Boolean);
            
            // Check if any submission is approved (from deliverable_submissions.status)
            const hasApprovedSubmission = subs.some((s: any) => s.status === 'approved');
            const hasPaidSubmission = subs.some((s: any) => s.status === 'paid');
            
            // Determine the correct status - prioritize deliverable_submissions status
            let finalStatus = 'submitted';
            let finalDeliverableStatus = appData?.deliverable_status || 'submitted';
            
            if (hasPaidSubmission || appData?.deliverable_status === 'paid') {
              finalStatus = 'paid';
              finalDeliverableStatus = 'paid';
            } else if (hasApprovedSubmission || appData?.deliverable_status === 'approved') {
              finalStatus = 'approved';
              finalDeliverableStatus = 'approved';
            } else if (subs.some((s: any) => s.status === 'revision_requested') || appData?.status === 'revision_requested') {
              finalStatus = 'revision_requested';
            }
            
            console.log('📊 Status mapping for', appId, '- submissions status:', subs.map((s: any) => s.status), 'appData status:', appData?.status, 'deliverable_status:', appData?.deliverable_status, '-> final:', finalStatus);
            
            // Collect all uploaded file URLs from all submissions
            // Check multiple possible field names for uploaded files
            const uploadedFileUrls = subs.map((s: any) => s.uploaded_file_url).filter(Boolean);
            const thumbnailUrls = subs.map((s: any) => s.thumbnail_url).filter(Boolean);
            
            console.log('📁 From deliverable_submissions - uploaded_file_urls:', uploadedFileUrls, 'thumbnails:', thumbnailUrls, 'content_urls:', contentUrls);
            
            // ALSO check campaign_applications.deliverable_data for uploaded files
            let deliverableDataFiles: string[] = [];
            let deliverableDataSocialLinks: any = {};
            let deliverableDataCaption = '';
            let deliverableDataHashtags: string[] = [];
            
            if (appData?.deliverable_data) {
              try {
                const parsed = typeof appData.deliverable_data === 'string' 
                  ? JSON.parse(appData.deliverable_data) 
                  : appData.deliverable_data;
                console.log('📦 Parsed deliverable_data from campaign_applications:', parsed);
                deliverableDataFiles = parsed.uploaded_files || parsed.files || [];
                deliverableDataSocialLinks = parsed.social_links || parsed.socialLinks || {};
                deliverableDataCaption = parsed.caption || parsed.notes || '';
                deliverableDataHashtags = parsed.hashtags || [];
              } catch (e) {
                console.log('⚠️ Could not parse deliverable_data:', e);
              }
            }
            
            // Merge files from ALL sources (deliverable_submissions fields + campaign_applications.deliverable_data + content_urls)
            const allUploadedFiles = [...new Set([
              ...uploadedFileUrls, 
              ...deliverableDataFiles,
              ...contentUrls  // content_url from deliverable_submissions
            ])];
            console.log('📁 All uploaded files (merged):', allUploadedFiles);
            
            // Separate images and videos
            const allFiles = [...allUploadedFiles, ...thumbnailUrls];
            const imageFiles = allFiles.filter((url: string) => 
              /\.(jpg|jpeg|png|gif|webp)/i.test(url)
            );
            const videoFiles = allFiles.filter((url: string) => 
              /\.(mp4|mov|avi|webm|mkv)/i.test(url)
            );
            
            // If no extension detected but URLs exist, treat them as content links (not necessarily images)
            // For Instagram/social links, they should be displayed as links, not images
            const socialLinkDomains = ['instagram.com', 'youtube.com', 'tiktok.com', 'facebook.com', 'twitter.com', 'x.com'];
            const contentLinks = allUploadedFiles.filter((url: string) => 
              socialLinkDomains.some(domain => url.includes(domain))
            );
            const mediaFiles = allUploadedFiles.filter((url: string) => 
              !socialLinkDomains.some(domain => url.includes(domain))
            );
            
            // Screenshots: media files that are images, or all media files if no extension detected
            const screenshots = imageFiles.length > 0 ? imageFiles : 
              (mediaFiles.length > 0 && videoFiles.length === 0 ? mediaFiles : []);
            
            // Collect all hashtags from all submissions + deliverable_data
            const allHashtags = [...new Set([
              ...subs.flatMap((s: any) => s.hashtags || []).filter(Boolean),
              ...deliverableDataHashtags
            ])];
            
            // Collect descriptions/notes
            const descriptions = subs.map((s: any) => s.description || s.notes).filter(Boolean);
            const finalCaption = deliverableDataCaption || descriptions.join('\n') || `${subs.map((s: any) => s.deliverable_type).join(', ')} - ${subs.length} item(s) submitted`;
            
            // Build social_links object from content links
            const socialLinksFromUrls: { [key: string]: string } = { ...deliverableDataSocialLinks };
            contentLinks.forEach((url: string, idx: number) => {
              if (url.includes('instagram.com')) {
                if (url.includes('/reel/')) {
                  socialLinksFromUrls['instagram_reel'] = url;
                } else {
                  socialLinksFromUrls['instagram_post'] = url;
                }
              } else if (url.includes('youtube.com') || url.includes('youtu.be')) {
                socialLinksFromUrls['youtube'] = url;
              } else if (url.includes('tiktok.com')) {
                socialLinksFromUrls['tiktok'] = url;
              } else {
                socialLinksFromUrls[`link_${idx}`] = url;
              }
            });
            
            console.log('📸 Final screenshots:', screenshots, 'videos:', videoFiles, 'hashtags:', allHashtags, 'socialLinks:', socialLinksFromUrls);
            
            return {
              id: appId,
              campaign_id: campaignId,
              creator_id: firstSub.creator_id,
              creator: {
                id: firstSub.creator_id,
                full_name: creatorName,
                avatar_url: creatorAvatar,
                username: creatorName,
              },
              post_link: contentLinks.find((u: string) => u.includes('instagram.com') && !u.includes('/reel/')) || contentUrls[0] || '',
              reel_link: contentLinks.find((u: string) => u.includes('/reel/') || u.includes('tiktok.com') || u.includes('youtube.com')) || '',
              content_url: contentUrls[0] || '',
              content_urls: contentUrls,
              // Include uploaded files and screenshots
              uploaded_files: allUploadedFiles,
              screenshots: screenshots,
              video_files: videoFiles,
              thumbnail_url: thumbnailUrls[0] || '',
              hashtags: allHashtags,
              caption: finalCaption,
              notes: descriptions.join('\n'),
              social_links: socialLinksFromUrls,
              deliverable_type: subs.map((s: any) => s.deliverable_type).join(', '),
              submitted_at: firstSub.submitted_at,
              status: finalStatus,
              deliverable_status: finalDeliverableStatus,
              submission_count: subs.length,
            };
          })
        );
        
        return transformedSubmissions;
      }
      
      // FALLBACK: Get applications that have submitted work (old method)
      // Check for: status = 'completed' OR deliverable_status = 'submitted' OR has deliverable_data
      console.log('🔄 Fetching from campaign_applications...');
      const { data: appData, error: appError } = await supabase
        .from('campaign_applications')
        .select('*')
        .eq('campaign_id', campaignId)
        .or('status.eq.completed,deliverable_status.eq.submitted,deliverable_status.eq.approved,deliverable_status.eq.paid,deliverable_data.neq.null')
        .order('created_at', { ascending: false });

      if (appError) {
        console.error('❌ Error fetching from campaign_applications:', appError);
        
        // Fallback: Try simpler query without .or()
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('campaign_applications')
          .select('*')
          .eq('campaign_id', campaignId)
          .not('deliverable_data', 'is', null)
          .order('created_at', { ascending: false });
        
        if (fallbackError) {
          console.error('❌ Fallback query also failed:', fallbackError);
          return [];
        }
        
        console.log('✅ Found applications (fallback):', fallbackData?.length || 0);
        if (!fallbackData || fallbackData.length === 0) return [];
        return await this.transformSubmissionData(fallbackData);
      }

      console.log('✅ Found applications:', appData?.length || 0, appData);

      if (!appData || appData.length === 0) {
        return [];
      }

      return await this.transformSubmissionData(appData);
    } catch (error) {
      console.error('❌ Error fetching submissions:', error);
      return [];
    }
  },

  async transformSubmissionData(appData: any[]) {
    // Enrich each submission with creator data
    const enrichedData = await Promise.all(
      appData.map(async (app: any) => {
        let creatorName = 'Creator';
        let creatorAvatar = '';
        let creatorUsername = '';
        
        try {
          // First, try to get creator_profiles by id (creator_id might be creator_profiles.id)
          const { data: creatorProfile } = await supabase
            .from('creator_profiles')
            .select('id, user_id, username, display_name, avatar_url, full_name')
            .eq('id', app.creator_id)
            .single();
          
          console.log('📋 Creator profile by id for', app.creator_id, ':', creatorProfile);
          
          if (creatorProfile) {
            // Found by creator_profiles.id - now get user_profiles
            creatorUsername = creatorProfile.username || '';
            creatorAvatar = creatorProfile.avatar_url || '';
            creatorName = creatorProfile.display_name || creatorProfile.full_name || creatorProfile.username || 'Creator';
            
            if (creatorProfile.user_id) {
              const { data: userProfile } = await supabase
                .from('user_profiles')
                .select('id, full_name, avatar_url, username')
                .eq('id', creatorProfile.user_id)
                .single();
              
              if (userProfile) {
                creatorName = userProfile.full_name || userProfile.username || creatorName;
                creatorAvatar = userProfile.avatar_url || creatorAvatar;
              }
            }
          } else {
            // Not found by creator_profiles.id - try by user_id (creator_id might be auth user id)
            console.log('🔄 Trying creator_profiles by user_id:', app.creator_id);
            const { data: creatorByUserId } = await supabase
              .from('creator_profiles')
              .select('id, user_id, username, display_name, avatar_url, full_name')
              .eq('user_id', app.creator_id)
              .single();
            
            console.log('📋 Creator profile by user_id:', creatorByUserId);
            
            if (creatorByUserId) {
              creatorUsername = creatorByUserId.username || '';
              creatorAvatar = creatorByUserId.avatar_url || '';
              creatorName = creatorByUserId.display_name || creatorByUserId.full_name || creatorByUserId.username || 'Creator';
            }
            
            // Also try user_profiles directly (creator_id might be user_profiles.id)
            const { data: userProfile } = await supabase
              .from('user_profiles')
              .select('id, full_name, avatar_url, username')
              .eq('id', app.creator_id)
              .single();
            
            console.log('👤 User profile directly for', app.creator_id, ':', userProfile);
            
            if (userProfile) {
              creatorName = userProfile.full_name || userProfile.username || creatorName;
              creatorAvatar = userProfile.avatar_url || creatorAvatar;
              creatorUsername = userProfile.username || creatorUsername;
            }
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch creator info for:', app.creator_id, err);
        }
        
        console.log(`✅ Final creator name for ${app.id}: ${creatorName}`);
        
        // Get files from DB column first
        let uploadedFiles: string[] = [];
        let socialLinks: any = {};
        
        if (app.deliverable_data) {
          try {
            const parsed = typeof app.deliverable_data === 'string' 
              ? JSON.parse(app.deliverable_data) 
              : app.deliverable_data;
            uploadedFiles = parsed.uploaded_files || [];
            socialLinks = parsed.social_links || {};
          } catch (e) {
            console.log('Could not parse deliverable_data for', app.id);
          }
        }
        
        // Fallback: Check localStorage (same browser only)
        if (uploadedFiles.length === 0) {
          const localData = localStorage.getItem(`deliverables_${app.id}`);
          if (localData) {
            try {
              const parsed = JSON.parse(localData);
              uploadedFiles = parsed.uploaded_files || [];
              socialLinks = parsed.social_links || {};
            } catch (e) {
              console.log('Could not parse localStorage for', app.id);
            }
          }
        }
        
        // Determine effective status - if deliverable was resubmitted after revision request,
        // show as pending for brand review
        let effectiveStatus = app.status;
        if (app.status === 'revision_requested' && app.deliverable_status === 'submitted') {
          // Creator has resubmitted after revision request - show as pending review
          effectiveStatus = 'pending';
          console.log(`📝 Application ${app.id}: Resubmitted after revision, showing as pending`);
        }

        return {
          id: app.id,
          campaign_id: app.campaign_id,
          creator_id: app.creator_id,
          creator: {
            id: app.creator_id,
            full_name: creatorName,
            username: creatorUsername,
            avatar_url: creatorAvatar
          },
          // Use effective status that accounts for resubmissions
          status: effectiveStatus,
          deliverable_status: app.deliverable_status,
          payment_status: app.payment_status,
          revision_comments: app.revision_comments,
          resubmitted: app.status === 'revision_requested' && app.deliverable_status === 'submitted',
          submitted_at: app.created_at,
          created_at: app.created_at,
          uploaded_files: uploadedFiles,
          social_media_links: socialLinks,
          notes: app.notes || '',
          caption: app.caption || ''
        };
      })
    );

    return enrichedData;
  },

  async approveSubmission(submissionId: string) {
    try {
      console.log('🔍 approveSubmission called with ID:', submissionId);
      
      // The submissionId is actually the application_id from getSubmissions transformation
      // First try to find and update deliverable_submissions by application_id
      const { data: deliverableSubs, error: findError } = await supabase
        .from('deliverable_submissions')
        .select('*, application_id, creator_id, campaign_id')
        .eq('application_id', submissionId);

      if (!findError && deliverableSubs && deliverableSubs.length > 0) {
        console.log('✅ Found', deliverableSubs.length, 'submissions for application:', submissionId);
        
        // Update all deliverable_submissions for this application
        const { error: updateError } = await supabase
          .from('deliverable_submissions')
          .update({ status: 'approved' })
          .eq('application_id', submissionId);
        
        if (updateError) {
          console.log('⚠️ Error updating deliverable_submissions:', updateError);
        } else {
          console.log('✅ Updated all deliverable_submissions to approved');
        }
        
        const firstSub = deliverableSubs[0];
        
        // IMPORTANT: Update campaign_applications.deliverable_status
        const { error: appUpdateError } = await supabase
          .from('campaign_applications')
          .update({ 
            deliverable_status: 'approved',
            updated_at: new Date().toISOString()
          })
          .eq('id', submissionId);
        
        if (appUpdateError) {
          console.log('⚠️ Could not update campaign_applications:', appUpdateError);
        } else {
          console.log('✅ Updated campaign_applications.deliverable_status to approved');
        }
        
        // Update creator status to completed
        await supabase
          .from('campaign_creators')
          .update({ status: 'completed' })
          .eq('campaign_id', firstSub.campaign_id)
          .eq('creator_id', firstSub.creator_id);
        
        return { ...firstSub, status: 'approved' };
      }

      // Try by direct ID in deliverable_submissions
      const { data: deliverableSub, error: delError } = await supabase
        .from('deliverable_submissions')
        .update({ status: 'approved' })
        .eq('id', submissionId)
        .select('*, application_id, creator_id, campaign_id')
        .single();

      if (!delError && deliverableSub) {
        console.log('✅ Updated deliverable_submissions by direct ID:', deliverableSub.id);
        
        // Also update campaign_applications.deliverable_status
        if (deliverableSub.application_id) {
          await supabase
            .from('campaign_applications')
            .update({ 
              deliverable_status: 'approved',
              updated_at: new Date().toISOString()
            })
            .eq('id', deliverableSub.application_id);
        }
        
        return deliverableSub;
      }

      // Try content_submissions table
      const { data: submission, error: subError } = await supabase
        .from('content_submissions')
        .update({ status: 'approved' })
        .eq('id', submissionId)
        .select()
        .single();

      if (!subError && submission) {
        // Update creator status to completed
        await supabase
          .from('campaign_creators')
          .update({ status: 'completed' })
          .eq('campaign_id', submission.campaign_id)
          .eq('creator_id', submission.creator_id);
        return submission;
      }

      // Fallback: Use RPC to handle enum properly for campaign_applications
      try {
        const { data: rpcResult, error: rpcError } = await supabase.rpc('update_application_status', {
          app_id: submissionId,
          new_status: 'completed',
          deliverable_json: { deliverable_status: 'approved' }
        });
        
        if (!rpcError) {
          // Get the updated application
          const { data: application } = await supabase
            .from('campaign_applications')
            .select('*')
            .eq('id', submissionId)
            .single();
          
          // Also update campaign_creators status
          if (application) {
            await supabase
              .from('campaign_creators')
              .update({ status: 'completed' })
              .eq('campaign_id', application.campaign_id)
              .eq('creator_id', application.creator_id);
          }
          
          return application;
        }
      } catch (rpcErr) {
        console.log('⚠️ RPC not available, using direct update');
      }

      // Fallback: Direct update with non-enum fields only
      const { data: application, error: appError } = await supabase
        .from('campaign_applications')
        .update({ 
          deliverable_status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', submissionId)
        .select()
        .single();

      if (appError) throw appError;
      
      // Also update campaign_creators status
      if (application) {
        await supabase
          .from('campaign_creators')
          .update({ status: 'completed' })
          .eq('campaign_id', application.campaign_id)
          .eq('creator_id', application.creator_id);
      }
      
      return application;
    } catch (error) {
      console.error('Error approving submission:', error);
      throw error;
    }
  },

  async requestRevision(submissionId: string, feedback: string) {
    try {
      console.log('📝 Requesting revision for:', submissionId, 'feedback:', feedback);
      
      // First, check if there are deliverable_submissions for this application_id
      // and update them
      const { data: deliverableSubs } = await supabase
        .from('deliverable_submissions')
        .select('*')
        .eq('application_id', submissionId);
      
      if (deliverableSubs && deliverableSubs.length > 0) {
        console.log('📦 Found', deliverableSubs.length, 'deliverable_submissions to update');
        await supabase
          .from('deliverable_submissions')
          .update({ status: 'revision_requested' })
          .eq('application_id', submissionId);
      }
      
      // Try RPC first to handle the application_status enum properly
      let rpcSuccess = false;
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('update_application_status', {
          app_id: submissionId,
          new_status: 'revision_requested',
          deliverable_json: {
            revision_comments: feedback,
            deliverable_status: 'submitted' // Keep as submitted, status field tracks revision
          }
        });
        
        if (!rpcError) {
          rpcSuccess = true;
          console.log('✅ Revision requested via RPC:', rpcData);
        } else {
          console.log('⚠️ RPC error:', rpcError.message);
        }
      } catch (rpcErr) {
        console.log('⚠️ RPC not available:', rpcErr);
      }
      
      // Fallback: Update using direct query
      if (!rpcSuccess) {
        // Try direct update with status as string (might work if status is text type)
        console.log('🔄 Trying direct status update...');
        const { error: directError } = await supabase
          .from('campaign_applications')
          .update({ 
            status: 'revision_requested',
            revision_comments: feedback,
            updated_at: new Date().toISOString()
          })
          .eq('id', submissionId);

        if (directError) {
          console.log('⚠️ Direct update failed:', directError.message);
          
          // Last fallback: Just save revision_comments, the status might be enum
          const { error: commentError } = await supabase
            .from('campaign_applications')
            .update({ 
              revision_comments: feedback,
              updated_at: new Date().toISOString()
            })
            .eq('id', submissionId);

          if (commentError) {
            console.error('❌ Error saving comments:', commentError);
            throw commentError;
          }
          
          console.log('⚠️ Only revision_comments saved, status update failed');
        } else {
          console.log('✅ Direct status update succeeded');
          rpcSuccess = true;
        }
      }
      
      // Update campaign_creators status
      const { data: app } = await supabase
        .from('campaign_applications')
        .select('campaign_id, creator_id')
        .eq('id', submissionId)
        .single();
        
      if (app) {
        await supabase
          .from('campaign_creators')
          .update({ status: 'working', updated_at: new Date().toISOString() })
          .eq('campaign_id', app.campaign_id)
          .eq('creator_id', app.creator_id);
      }
      
      console.log('✅ Revision requested successfully');
      return { success: true, message: 'Revision requested' };
    } catch (error) {
      console.error('Error requesting revision:', error);
      throw error;
    }
  },

  async releasePayment(submissionId: string) {
    try {
      console.log('💰 Releasing payment for submission:', submissionId);
      
      // First, update deliverable_submissions if they exist (for mobile app submissions)
      const { data: deliverableSubs } = await supabase
        .from('deliverable_submissions')
        .select('*, campaign_id, creator_id, application_id')
        .eq('application_id', submissionId);
      
      if (deliverableSubs && deliverableSubs.length > 0) {
        console.log('📦 Found', deliverableSubs.length, 'deliverable_submissions, updating to paid');
        await supabase
          .from('deliverable_submissions')
          .update({ status: 'paid' })
          .eq('application_id', submissionId);
      }
      
      // Get the application/submission details
      const { data: application, error: fetchError } = await supabase
        .from('campaign_applications')
        .select('*, campaigns(budget)')
        .eq('id', submissionId)
        .single();

      if (fetchError || !application) {
        console.log('⚠️ Application not found directly, using deliverable_submissions data');
        
        // Use deliverable_submissions data to update campaign_applications
        if (deliverableSubs && deliverableSubs.length > 0) {
          const firstSub = deliverableSubs[0];
          const appId = firstSub.application_id;
          const campaignId = firstSub.campaign_id;
          const creatorId = firstSub.creator_id;
          
          console.log('📝 Updating campaign_applications directly with id:', appId);
          
          // Update campaign_applications directly
          const { error: updateError } = await supabase
            .from('campaign_applications')
            .update({
              deliverable_status: 'paid',
              payment_status: 'paid',
              payment_date: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', appId);
          
          if (updateError) {
            console.log('⚠️ Direct update error:', updateError);
            // Try RPC as fallback
            try {
              await supabase.rpc('update_application_status', {
                app_id: appId,
                new_status: 'completed',
                deliverable_json: {
                  deliverable_status: 'paid',
                  payment_status: 'paid',
                  payment_date: new Date().toISOString()
                }
              });
              console.log('✅ Updated via RPC');
            } catch (rpcErr) {
              console.log('⚠️ RPC also failed:', rpcErr);
            }
          } else {
            console.log('✅ Updated campaign_applications.deliverable_status to paid');
          }
          
          // Update campaign_creators
          await supabase
            .from('campaign_creators')
            .update({ status: 'completed' })
            .eq('campaign_id', campaignId)
            .eq('creator_id', creatorId);
          
          // Update payments table to completed
          const { data: existingPayment } = await supabase
            .from('payments')
            .select('id')
            .eq('campaign_id', campaignId)
            .eq('creator_id', creatorId)
            .maybeSingle();
          
          if (existingPayment) {
            await supabase
              .from('payments')
              .update({
                status: 'completed',
                paid_at: new Date().toISOString()
              })
              .eq('id', existingPayment.id);
            console.log('✅ Payment record updated to completed');
          }
          
          console.log('✅ Payment release completed via deliverable_submissions path');
          return { success: true, data: { id: appId, campaign_id: campaignId, creator_id: creatorId } };
        }
        
        throw new Error('Submission not found');
      }
      
      return this.processPaymentRelease(application, submissionId);
    } catch (error) {
      console.error('Error releasing payment:', error);
      throw error;
    }
  },

  async processPaymentRelease(application: any, submissionId: string) {
    try {
      console.log('📋 Processing payment for application:', application.id);
      
      // Use application.id for the updates (this is the campaign_applications id)
      const appId = application.id;

      // Try RPC first to handle enum properly
      let rpcSuccess = false;
      try {
        const { error: rpcError } = await supabase.rpc('update_application_status', {
          app_id: appId,
          new_status: 'completed',
          deliverable_json: {
            deliverable_status: 'paid',
            payment_status: 'paid',
            payment_date: new Date().toISOString()
          }
        });
        if (!rpcError) {
          rpcSuccess = true;
          console.log('✅ Payment status updated via RPC');
        }
      } catch (rpcErr) {
        console.log('⚠️ RPC not available');
      }

      // Fallback: Update non-enum fields only
      if (!rpcSuccess) {
        const { error: updateError } = await supabase
          .from('campaign_applications')
          .update({
            deliverable_status: 'paid',
            payment_status: 'paid',
            payment_date: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', appId);

        if (updateError) {
          console.error('❌ Error updating application:', updateError);
          throw updateError;
        }
      }
      
      console.log('✅ Application payment status updated');

      // Also update campaign_creators status to completed
      const { error: creatorUpdateError } = await supabase
        .from('campaign_creators')
        .update({ status: 'completed' })
        .eq('campaign_id', application.campaign_id)
        .eq('creator_id', application.creator_id);
        
      if (creatorUpdateError) {
        console.warn('⚠️ Could not update campaign_creators:', creatorUpdateError);
      }

      // 🔥 AUTO-PAYOUT: Trigger automatic payment to creator
      const payoutAmount = application.payout_offered || application.proposed_budget || application.campaigns?.budget || 0;
      
      if (payoutAmount > 0) {
        console.log('💸 Triggering auto-payout to creator:', {
          applicationId: appId,
          creatorId: application.creator_id,
          amount: payoutAmount
        });

        try {
          const { data: payoutResult, error: payoutError } = await supabase.functions.invoke('auto-payout-creator', {
            body: {
              applicationId: appId,
              campaignId: application.campaign_id,
              creatorId: application.creator_id,
              grossAmount: payoutAmount,
            }
          });

          if (payoutError) {
            console.error('⚠️ Auto-payout failed:', payoutError);
          } else {
            console.log('✅ Auto-payout result:', payoutResult);
          }
        } catch (payoutErr) {
          console.error('⚠️ Auto-payout error:', payoutErr);
          // Don't fail the whole operation if payout fails
        }
      }

      // Update existing payment record to completed (or create if doesn't exist)
      try {
        // First try to update existing payment
        const { data: existingPayment, error: findError } = await supabase
          .from('payments')
          .select('id')
          .eq('campaign_id', application.campaign_id)
          .eq('creator_id', application.creator_id)
          .maybeSingle();
        
        if (existingPayment) {
          // Update existing payment to completed
          await supabase
            .from('payments')
            .update({
              status: 'completed',
              paid_at: new Date().toISOString(),
              notes: 'Payment released after content approval'
            })
            .eq('id', existingPayment.id);
          console.log('✅ Payment record updated to completed');
        } else {
          // Create new payment record
          await supabase.from('payments').insert({
            campaign_id: application.campaign_id,
            creator_id: application.creator_id,
            amount: payoutAmount,
            status: 'completed',
            paid_at: new Date().toISOString(),
            notes: 'Auto-payout triggered after content approval'
          });
          console.log('✅ Payment record created as completed');
        }
      } catch (e) {
        console.log('⚠️ Payments table update error:', e);
      }

      return { success: true, data: application };
    } catch (error) {
      console.error('Error releasing payment:', error);
      throw error;
    }
  },

  // Payments Management
  async getPayments(campaignId: string) {
    try {
      // First, try to get from payments table
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      // Get payment info from campaign_creators (approved creators with payment amounts)
      const { data: creatorsData, error: creatorsError } = await supabase
        .from('campaign_creators')
        .select(`
          id,
          creator_id,
          payment_amount,
          payment_status,
          status
        `)
        .eq('campaign_id', campaignId);

      // Also get from applications for any additional info
      const { data: applicationsData, error: appError } = await supabase
        .from('campaign_applications')
        .select(`
          id,
          creator_id,
          payout_offered,
          proposed_budget,
          payment_status,
          payment_date,
          deliverable_status,
          status,
          created_at,
          shipping_address,
          shipping_city,
          shipping_state,
          shipping_zip_code,
          shipping_country,
          shipped,
          shipped_at,
          tracking_notes,
          product_received,
          product_received_at
        `)
        .eq('campaign_id', campaignId)
        .in('status', ['approved', 'completed', 'submitted']);

      console.log('📦 Applications data for campaign:', campaignId, applicationsData);
      console.log('📍 First app shipping:', applicationsData?.[0]?.shipping_address);

      // Build a map of creator_id to payment_amount from campaign_creators
      const creatorPaymentMap = new Map(
        (creatorsData || []).map(c => [c.creator_id, c.payment_amount || 0])
      );

      // Combine both sources
      let allPayments: any[] = [];
      
      // Add payments from payments table
      if (!paymentsError && paymentsData && paymentsData.length > 0) {
        console.log('💰 Payments from payments table:', paymentsData);
        allPayments = [...paymentsData];
      }

      // Build a map of shipping data from applications
      const applicationShippingMap = new Map(
        (applicationsData || []).map(app => [app.creator_id, {
          shipping_address: app.shipping_address,
          shipping_city: app.shipping_city,
          shipping_state: app.shipping_state,
          shipping_zip_code: app.shipping_zip_code,
          shipping_country: app.shipping_country,
          shipped: app.shipped,
          shipped_at: app.shipped_at,
          tracking_notes: app.tracking_notes,
          product_received: app.product_received,
          product_received_at: app.product_received_at
        }])
      );

      // Merge shipping data into existing payments
      allPayments = allPayments.map(payment => {
        const shippingData = applicationShippingMap.get(payment.creator_id);
        if (shippingData) {
          return {
            ...payment,
            shipping_address: payment.shipping_address || shippingData.shipping_address,
            shipping_city: payment.shipping_city || shippingData.shipping_city,
            shipping_state: payment.shipping_state || shippingData.shipping_state,
            shipping_zip_code: payment.shipping_zip_code || shippingData.shipping_zip_code,
            shipping_country: payment.shipping_country || shippingData.shipping_country,
            shipped: payment.shipped ?? shippingData.shipped ?? false,
            shipped_at: payment.shipped_at || shippingData.shipped_at,
            tracking_notes: payment.tracking_notes || shippingData.tracking_notes,
            product_received: payment.product_received ?? shippingData.product_received ?? false,
            product_received_at: payment.product_received_at || shippingData.product_received_at
          };
        }
        return payment;
      });

      // Add pending payments from applications (if not already in payments table)
      if (!appError && applicationsData && applicationsData.length > 0) {
        const existingCreatorIds = new Set(allPayments.map(p => p.creator_id));
        console.log('🔍 Existing creator IDs from payments:', [...existingCreatorIds]);
        console.log('📋 Applications with shipping:', applicationsData.map(a => ({
          id: a.id,
          creator_id: a.creator_id,
          shipping_address: a.shipping_address,
          status: a.status
        })));
        
        const applicationPayments = applicationsData
          .filter(app => !existingCreatorIds.has(app.creator_id))
          .map(app => {
            // Priority: campaign_creators.payment_amount > payout_offered > proposed_budget
            const paymentAmount = creatorPaymentMap.get(app.creator_id) 
              || app.payout_offered 
              || app.proposed_budget 
              || 0;
            
            return {
              id: app.id, // Use application ID as payment ID
              campaign_id: campaignId,
              creator_id: app.creator_id,
              amount: paymentAmount,
              payment_method: 'UPI',
              status: app.payment_status === 'paid' ? 'paid' : 'pending',
              paid_at: app.payment_date,
              created_at: app.created_at,
              source: 'application', // Mark source for tracking
              // Shipping info for barter campaigns
              shipping_address: app.shipping_address,
              shipping_city: app.shipping_city,
              shipping_state: app.shipping_state,
              shipping_zip_code: app.shipping_zip_code,
              shipping_country: app.shipping_country,
              shipped: app.shipped || false,
              shipped_at: app.shipped_at,
              tracking_notes: app.tracking_notes,
              product_received: app.product_received || false,
              product_received_at: app.product_received_at
            };
          });
        
        allPayments = [...allPayments, ...applicationPayments];
      }

      if (allPayments.length === 0) return [];

      // Fetch creator profiles separately
      const creatorIds = [...new Set(allPayments.map(p => p.creator_id).filter(Boolean))];
      
      const { data: creatorProfiles } = await supabase
        .from('creator_profiles')
        .select('id, user_id, category, username')
        .in('id', creatorIds);
      
      // Get user profiles for names
      const userIds = creatorProfiles?.map(cp => cp.user_id).filter(Boolean) || [];
      const { data: userProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, avatar_url, username, email, phone')
        .in('id', userIds);

      // Build lookup maps
      const creatorToUser = new Map(creatorProfiles?.map(cp => [cp.id, cp.user_id]) || []);
      const userMap = new Map(userProfiles?.map(u => [u.id, u]) || []);

      // Enrich payments with creator info
      const enrichedPayments = allPayments.map(payment => {
        const userId = creatorToUser.get(payment.creator_id);
        const user = userId ? userMap.get(userId) : null;
        
        return {
          ...payment,
          creator: user ? {
            id: payment.creator_id,
            full_name: user.full_name || user.username || 'Unknown Creator',
            avatar_url: user.avatar_url,
            email: user.email,
            phone: user.phone
          } : null
        };
      });

      return enrichedPayments;
    } catch (error) {
      console.error('Error fetching payments:', error);
      return [];
    }
  },

  async markPaymentAsPaid(paymentId: string, notes?: string) {
    try {
      // First, check if this is a payment record or an application record
      const { data: existingPayment, error: checkError } = await supabase
        .from('payments')
        .select('id')
        .eq('id', paymentId)
        .single();

      if (existingPayment) {
        // Update existing payment record
        const { data, error } = await supabase
          .from('payments')
          .update({ 
            status: 'paid',
            paid_at: new Date().toISOString(),
            notes 
          })
          .eq('id', paymentId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // This is an application ID - update the application's payment_status
        const { data: appData, error: appError } = await supabase
          .from('campaign_applications')
          .update({ 
            payment_status: 'paid',
            payment_date: new Date().toISOString()
          })
          .eq('id', paymentId)
          .select('*, campaign_id, creator_id, payout_offered, proposed_budget')
          .single();

        if (appError) {
          console.error('Error updating application payment:', appError);
          throw new Error('Failed to update payment status. The record may not exist.');
        }

        // Also update campaign_creators if exists
        if (appData?.campaign_id && appData?.creator_id) {
          await supabase
            .from('campaign_creators')
            .update({ 
              payment_status: 'paid',
              payment_date: new Date().toISOString()
            })
            .eq('campaign_id', appData.campaign_id)
            .eq('creator_id', appData.creator_id);
        }

        return {
          id: paymentId,
          status: 'paid',
          paid_at: new Date().toISOString(),
          notes,
          amount: appData?.payout_offered || appData?.proposed_budget || 0
        };
      }
    } catch (error: any) {
      console.error('Error marking payment as paid:', error);
      throw error;
    }
  },

  async markAsShipped(paymentOrApplicationId: string, notes?: string) {
    try {
      // First, try to find if this is an application ID directly
      const { data: appData, error: appCheckError } = await supabase
        .from('campaign_applications')
        .select('id')
        .eq('id', paymentOrApplicationId)
        .single();

      if (appData) {
        // It's an application ID - update directly
        const { data, error } = await supabase
          .from('campaign_applications')
          .update({ 
            shipped: true,
            shipped_at: new Date().toISOString(),
            tracking_notes: notes
          })
          .eq('id', paymentOrApplicationId)
          .select()
          .single();

        if (error) {
          console.error('Error marking as shipped (app):', error);
          throw new Error('Failed to update shipping status.');
        }
        return data;
      }

      // Otherwise, it might be a payment ID - find the creator and update their application
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('creator_id, campaign_id')
        .eq('id', paymentOrApplicationId)
        .single();

      if (paymentData) {
        // Update the application by creator_id and campaign_id
        const { data, error } = await supabase
          .from('campaign_applications')
          .update({ 
            shipped: true,
            shipped_at: new Date().toISOString(),
            tracking_notes: notes
          })
          .eq('creator_id', paymentData.creator_id)
          .eq('campaign_id', paymentData.campaign_id)
          .select()
          .single();

        if (error) {
          console.error('Error marking as shipped (payment lookup):', error);
          throw new Error('Failed to update shipping status.');
        }
        return data;
      }

      throw new Error('Could not find application or payment record.');
    } catch (error: any) {
      console.error('Error marking as shipped:', error);
      throw error;
    }
  },

  async updatePaymentAmount(campaignId: string, paymentId: string, amount: number) {
    try {
      // Update payment amount in payments table
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ amount })
        .eq('id', paymentId);

      // Also try to update in campaign_applications (payout_offered)
      // and campaign_creators (payment_amount)
      // Get the creator_id from the payment first
      const { data: paymentData } = await supabase
        .from('payments')
        .select('creator_id')
        .eq('id', paymentId)
        .single();

      if (paymentData?.creator_id) {
        // Update campaign_creators
        await supabase
          .from('campaign_creators')
          .update({ payment_amount: amount })
          .eq('campaign_id', campaignId)
          .eq('creator_id', paymentData.creator_id);

        // Update campaign_applications
        await supabase
          .from('campaign_applications')
          .update({ payout_offered: amount })
          .eq('campaign_id', campaignId)
          .eq('creator_id', paymentData.creator_id);
      }

      if (paymentError) throw paymentError;
      return { success: true };
    } catch (error) {
      console.error('Error updating payment amount:', error);
      throw error;
    }
  },

  // Messages Management
  async getMessageThreads(campaignId: string) {
    try {
      const { data, error } = await supabase
        .from('campaign_messages')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) {
        // If table doesn't exist yet, return empty array
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.log('campaign_messages table not found, returning empty array');
          return [];
        }
        throw error;
      }

      // Group by creator
      const threads = new Map();
      data?.forEach((msg: any) => {
        if (!threads.has(msg.creator_id)) {
          threads.set(msg.creator_id, {
            creator_id: msg.creator_id,
            last_message: msg.content,
            last_message_time: msg.created_at,
            unread_count: msg.is_read ? 0 : 1
          });
        } else if (!msg.is_read) {
          // Count unread messages
          const existing = threads.get(msg.creator_id);
          existing.unread_count = (existing.unread_count || 0) + 1;
        }
      });

      return Array.from(threads.values());
    } catch (error) {
      console.error('Error fetching message threads:', error);
      return [];
    }
  },

  async getMessages(campaignId: string, creatorId: string) {
    try {
      console.log('📨 getMessages called:', { campaignId, creatorId });
      
      const { data, error } = await supabase
        .from('campaign_messages')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('creator_id', creatorId)
        .order('created_at', { ascending: true });

      console.log('📨 getMessages result:', { count: data?.length, error });

      if (error) {
        // If table doesn't exist yet, return empty array
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          console.log('campaign_messages table not found');
          return [];
        }
        throw error;
      }
      
      // If no messages found, try alternative query with sender_id
      if (!data || data.length === 0) {
        console.log('📨 No messages with creator_id, trying sender_id lookup...');
        const { data: altData, error: altError } = await supabase
          .from('campaign_messages')
          .select('*')
          .eq('campaign_id', campaignId)
          .or(`sender_id.eq.${creatorId},creator_id.eq.${creatorId}`)
          .order('created_at', { ascending: true });
        
        console.log('📨 Alternative query result:', { count: altData?.length, error: altError });
        
        if (!altError && altData && altData.length > 0) {
          return altData;
        }
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  },

  async sendMessage(campaignId: string, creatorId: string, content: string, isBrand: boolean, senderId?: string) {
    try {
      // If senderId not provided, get current user
      let actualSenderId = senderId;
      if (!actualSenderId) {
        const { data: { user } } = await supabase.auth.getUser();
        actualSenderId = user?.id;
      }

      if (!actualSenderId) {
        throw new Error('sender_id is required');
      }

      // Get the creator's user_id from creator_profiles
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('user_id')
        .eq('id', creatorId)
        .single();

      if (profileError) {
        console.error('Error fetching creator profile:', profileError);
      }

      const creatorUserId = creatorProfile?.user_id;

      // Store in campaign_messages for campaign-specific context
      const { data, error } = await supabase
        .from('campaign_messages')
        .insert([{
          campaign_id: campaignId,
          creator_id: creatorId,
          sender_id: actualSenderId,
          content,
          is_brand: isBrand,
          is_read: false
        }])
        .select()
        .single();

      if (error) throw error;

      // Also send to the messages table for cross-platform sync (mobile app)
      if (creatorUserId) {
        try {
          // Get or create conversation between brand and creator
          const { data: existingConv } = await supabase
            .from('conversations')
            .select('id')
            .contains('participant_ids', [actualSenderId])
            .contains('participant_ids', [creatorUserId])
            .limit(1)
            .single();

          let conversationId = existingConv?.id;

          if (!conversationId) {
            // Create new conversation
            const { data: newConv, error: convError } = await supabase
              .from('conversations')
              .insert({
                participant_ids: [actualSenderId, creatorUserId],
                subject: 'Campaign Chat',
                last_message_at: new Date().toISOString()
              })
              .select()
              .single();

            if (convError) {
              console.error('Error creating conversation:', convError);
            } else {
              conversationId = newConv.id;
            }
          }

          if (conversationId) {
            // Determine recipient based on who is sending
            const recipientId = isBrand ? creatorUserId : actualSenderId;
            
            // Send the message to messages table
            const { error: msgError } = await supabase
              .from('messages')
              .insert({
                conversation_id: conversationId,
                sender_id: actualSenderId,
                recipient_id: recipientId,
                content: content,
                is_read: false
              });

            if (msgError) {
              console.error('Error sending message to messages table:', msgError);
            }

            // Update conversation last_message_at
            await supabase
              .from('conversations')
              .update({ last_message_at: new Date().toISOString() })
              .eq('id', conversationId);

            // Create notification for the recipient (creator) when brand sends message
            if (isBrand && creatorUserId) {
              try {
                // Get campaign name and brand name for notification
                const { data: campaignData } = await supabase
                  .from('campaigns')
                  .select('name')
                  .eq('id', campaignId)
                  .single();

                const { data: brandProfile } = await supabase
                  .from('user_profiles')
                  .select('full_name, company_name')
                  .eq('id', actualSenderId)
                  .single();

                const campaignName = campaignData?.name || 'Campaign';
                const brandName = brandProfile?.company_name || brandProfile?.full_name || 'Brand';
                const previewContent = content.length > 50 ? content.substring(0, 50) + '...' : content;

                // Create in-app notification
                await supabase
                  .from('notifications')
                  .insert({
                    user_id: creatorUserId,
                    type: 'new_message',
                    title: 'New Message from Brand',
                    message: `You have a new message regarding "${campaignName}": ${previewContent}`,
                    data: {
                      campaign_id: campaignId,
                      conversation_id: conversationId,
                      sender_id: actualSenderId
                    },
                    is_read: false
                  });
                
                // Send push notification (OneSignal) for when app is closed
                await oneSignalService.notifyNewMessage(
                  creatorUserId,
                  brandName,
                  content,
                  conversationId,
                  campaignId
                );
                
                console.log('Notification created for creator:', creatorUserId);
              } catch (notifError) {
                // Non-critical: don't fail if notification creation fails
                console.log('Note: Could not create notification:', notifError);
              }
            }
          }
        } catch (syncError) {
          // Non-critical: don't fail the main operation if sync fails
          console.error('Error syncing message to messages table:', syncError);
        }
      }

      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async broadcastMessage(campaignId: string, content: string, brandUserId?: string) {
    try {
      // Get the campaign to find the brand
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('brand_id')
        .eq('id', campaignId)
        .single();

      if (campaignError) throw campaignError;
      
      const senderUserId = brandUserId || campaign?.brand_id;
      if (!senderUserId) {
        throw new Error('Could not determine brand user ID');
      }

      // Get all creators in this campaign with their user_ids
      const { data: creators, error: creatorsError } = await supabase
        .from('campaign_creators')
        .select('creator_id')
        .eq('campaign_id', campaignId);

      if (creatorsError) throw creatorsError;

      if (!creators || creators.length === 0) {
        console.log('No creators found for campaign:', campaignId);
        return { success: true, count: 0 };
      }

      // Get creator user_ids from creator_profiles
      const creatorProfileIds = creators.map((c: any) => c.creator_id).filter(Boolean);
      const { data: creatorProfiles, error: profilesError } = await supabase
        .from('creator_profiles')
        .select('id, user_id')
        .in('id', creatorProfileIds);

      if (profilesError) throw profilesError;

      // Send messages through the chat system (conversations + messages tables)
      let sentCount = 0;
      for (const profile of creatorProfiles || []) {
        if (!profile.user_id) continue;
        
        try {
          // Get or create conversation between brand and creator
          const { data: existingConv } = await supabase
            .from('conversations')
            .select('id')
            .contains('participant_ids', [senderUserId])
            .contains('participant_ids', [profile.user_id])
            .limit(1)
            .single();

          let conversationId = existingConv?.id;
          
          if (!conversationId) {
            // Create new conversation
            const { data: newConv, error: convError } = await supabase
              .from('conversations')
              .insert({
                participant_ids: [senderUserId, profile.user_id],
                subject: 'Campaign Broadcast',
                last_message_at: new Date().toISOString()
              })
              .select()
              .single();
            
            if (convError) {
              console.error('Error creating conversation:', convError);
              continue;
            }
            conversationId = newConv.id;
          }

          // Send the message
          const { error: msgError } = await supabase
            .from('messages')
            .insert({
              conversation_id: conversationId,
              sender_id: senderUserId,
              recipient_id: profile.user_id,
              content: `📢 [Campaign Broadcast]\n\n${content}`,
              is_read: false
            });

          if (msgError) {
            console.error('Error sending message to creator:', msgError);
            continue;
          }

          // Update conversation last_message_at
          await supabase
            .from('conversations')
            .update({ last_message_at: new Date().toISOString() })
            .eq('id', conversationId);

          sentCount++;
        } catch (err) {
          console.error('Error sending to creator:', profile.user_id, err);
        }
      }

      // Also store in campaign_messages for campaign-specific context (optional)
      try {
        const campaignMessages = creators.map((c: any) => ({
          campaign_id: campaignId,
          creator_id: c.creator_id,
          sender_id: senderUserId,
          content,
          is_brand: true,
          is_read: false
        }));

        await supabase
          .from('campaign_messages')
          .insert(campaignMessages);
      } catch (err) {
        // campaign_messages is optional, don't fail if it doesn't work
        console.log('Note: campaign_messages insert skipped:', err);
      }

      // Create notifications for all creators
      try {
        // Get campaign name for notification
        const { data: campaignData } = await supabase
          .from('campaigns')
          .select('name')
          .eq('id', campaignId)
          .single();

        const campaignName = campaignData?.name || 'Campaign';
        const previewContent = content.length > 50 ? content.substring(0, 50) + '...' : content;

        // Create notifications for each creator
        const notifications = (creatorProfiles || [])
          .filter((p: any) => p.user_id)
          .map((p: any) => ({
            user_id: p.user_id,
            type: 'new_message',
            title: '📢 Campaign Broadcast',
            message: `New broadcast from "${campaignName}": ${previewContent}`,
            data: {
              campaign_id: campaignId,
              sender_id: senderUserId,
              is_broadcast: true
            },
            is_read: false
          }));

        if (notifications.length > 0) {
          await supabase
            .from('notifications')
            .insert(notifications);
          
          console.log(`Created ${notifications.length} broadcast notifications`);

          // Send push notifications to all creators (OneSignal)
          for (const profile of (creatorProfiles || [])) {
            if (profile.user_id) {
              try {
                await oneSignalService.notifyBroadcast(
                  profile.user_id,
                  campaignName,
                  content,
                  campaignId
                );
              } catch (pushErr) {
                // Non-critical: continue with other notifications
                console.log('Push notification skipped for:', profile.user_id);
              }
            }
          }
        }
      } catch (notifErr) {
        // Non-critical: don't fail if notification creation fails
        console.log('Note: broadcast notifications skipped:', notifErr);
      }

      return { success: true, count: sentCount };
    } catch (error) {
      console.error('Error broadcasting message:', error);
      throw error;
    }
  }
};